import {
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Alert,
  Chip,
  TablePagination,
  IconButton,
  DialogContent,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import axios, { AxiosResponse } from 'axios';
import { Box, Grid } from '@mui/material';
import { useState, useContext, useEffect } from 'react';
import { isMobile, isTablet,isDesktop  } from 'react-device-detect';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { PaginationContext } from '../../../contexts/pagination-context';
import { useSnackBar } from '../../../contexts/snackbar';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { DeliveryBatchState } from '../../../enums/tasks';
import { useApiConfigWithBranch } from '../../../hooks/use-api-config-with-branch';
import { IDeliveryBatch } from '../../../interfaces/delivery-company/tasks';
import ConfirmAction from '../../common/confirm-action/confirm-action';
import CustomDialog from '../../common/custom-dialog/custom-dialog';
import DoubleDatePicker from '../../common/double-date-picker/double-date-picker';
import { DoubleDate } from '../../common/double-date-picker/double-date-picker.interfaces';
import { ErrorsFlattenner } from '../../common/errors-flattenner/errors-flattenner';
import FilterBar from '../../common/filter-bar/filter-bar';
import { buildQueryParams } from '../../common/filter-bar/filter-bar.utils';
import { DeliveryBatchFilterKeys } from '../../common/filter-bar/keys-config';
import { IFilterOption } from '../../common/filter-bar/types';
import ResponsiveDeliveryBatchesListing, {
  delivery_batches_relevant_fields,
} from '../common/delivery-tasks-listing';
import FailedDeliveriesReceiver from '../failed-deliveries-receiver/failed-deliveries-receiver';
import DeliveryBatchForm from './create-task-form/create-delivery-task-form';
import DeliveryBatchDialog from './delivery-task-dialog';
import TaskWaybillsDialog from './tasks-listing/task-waybills-dialog';
import { ExpandMore, ManageSearch } from '@mui/icons-material';
import { DataResources, GlobalPermissions, ResourceActionType, user_has_data_model_access, user_has_global_permission } from '../../../utils/permissions';
import { useBranchFilter } from '../../../contexts/branch-filter-context';
import BranchFilterSelector from '../../common/branch-filter-selector/branch-filter-selector';

function DeliveryBatchesListing() {
  const [tasks, setTasks] = useState<IDeliveryBatch[]>([]);
  const [tasksCount, setTasksCount] = useState(0);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [downloadWaybillForTask, setDownloadWaybillForTask] =
    useState<IDeliveryBatch>();
  const [downloadReturnStatementsForTask, setDownloadReturnStatementsForTask] =
    useState<IDeliveryBatch>();
  const [downloadBatchQrcode, setDownloadBatchQrcode] =
    useState<IDeliveryBatch>();

  const [editingTask, setEditingTask] = useState<IDeliveryBatch>();
  const [cancellingTask, setCancellingTask] = useState<IDeliveryBatch>();
  const [completingTask, setCompletingTask] = useState<IDeliveryBatch>();
  const [response, setResponse] = useState<AxiosResponse>();
  const [message, setMessage] = useState<string>('');
  const [filters, setFilters] = useState<IFilterOption[]>([]);
  const [creatingNewTask, setCreatingNewTask] = useState(false);
  const [receivingFailedOrders, setReceivingFailedOrders] = useState(false);
  const { showMessage } = useSnackBar();

  const [dateFilter, setDateFilter] = useState<DoubleDate>({
    startDate: null,
    endDate: null,
  });

  const { user } = useContext(AuthContext);
  const { config, branchKey } = useApiConfigWithBranch();
  const { showAllBranches } = useBranchFilter();

  const { paginationConfig, setPaginationConfig } =
    useContext(PaginationContext);

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: paginationConfig.rowsPerPage,
  });

  useEffect(() => {
    const getData = async () => {
      let endpoint = `${API_ENDPOINT}/api/v1/delivery-batches/?page=${controller.page + 1}&page_size=${controller.rowsPerPage}&fields=${delivery_batches_relevant_fields.join(',')}`;

      const filterQueryParams = buildQueryParams(filters);
      endpoint += `${filterQueryParams}`;

      const { startDate, endDate } = dateFilter;

      if (startDate !== null) {
        endpoint += `&date_from=${encodeURIComponent(startDate.format('YYYY-MM-DD'))}`;
      }

      if (endDate !== null) {
        endpoint += `&date_to=${encodeURIComponent(endDate.format('YYYY-MM-DD'))}`;
      }

      if (showAllBranches) {
        endpoint += `&all_branches=true`;
      }

      try {
        console.log(endpoint);
        const response = await axios.get<{
          count: number;
          results: IDeliveryBatch[];
        }>(endpoint, config);
        console.log('response = ', response);
        setTasks(response.data.results);
        setTasksCount(response.data.count);
      } catch (error) {
        console.log(error);
      }
      setIsLoading(false);
    };
    setIsLoading(true);
    getData();
  }, [controller, branchKey, showAllBranches]);

  const handleCancelTask = async () => {
    try {
      setMessage('');
      setResponse(undefined);
      setIsLoading(true);
      const endpoint = `${API_ENDPOINT}/api/v1/delivery-batch/${cancellingTask?.id}/`;
      const response = await axios.delete(endpoint, config);
      setResponse(response);
      setTasks(
        tasks.map((task) =>
          task.id === cancellingTask?.id
            ? { ...task, status: DeliveryBatchState.CANCELLED }
            : task
        )
      );
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        setMessage(
          'Failed to cancel pickup request: ' + e.response?.statusText
        );
      } else {
        setMessage('Failed to cancel pickup request');
      }
    }
    setIsLoading(false);
  };

  const handleCompleteTask = async () => {
    try {
      setResponse(undefined);
      setIsLoading(true);
      const endpoint = `${API_ENDPOINT}/api/v1/delivery-batch/${completingTask?.id}/complete/`;
      const response = await axios.post(endpoint, {}, config);
      setResponse(response);
      setTasks(
        tasks.map((task) =>
          task.id === completingTask?.id
            ? { ...task, status: DeliveryBatchState.OUT_FOR_DELIVERY }
            : task
        )
      );
      showMessage("Batch marked as collected.")
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
      } 
      showMessage('Failed to mark batch as collected.', 'error');
    }
    setIsLoading(false);
  }

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPaginationConfig({ ...paginationConfig, rowsPerPage: newRowsPerPage });
    setController({
      ...controller,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };

  const resetPaginationControls = () => {
    setController({
      ...controller,
      page: 0,
    });
  };

  const resetAll = () => {
    resetPaginationControls();
  };

  const filterQueryAction = (filters: IFilterOption[]) => {
    resetPaginationControls();
    setFilters(filters);
  };

  const dateFilterAction = (dateFilterValue: DoubleDate) => {
    resetPaginationControls();
    setDateFilter(dateFilterValue);
  };

  return (
    <div>
      <Box>
        <Typography
          variant="h3"
          component="h3"
          sx={{ textAlign: 'center', mt: 3, mb: 1 }}
        >
          Delivery Batches
        </Typography>

        <BranchFilterSelector />

        <br />

        {
          <Grid container spacing={2}>
            <Grid
              xs={12}
              sx={{
                textAlign: 'right',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
              }}
            >
              <Button
                size={(isMobile && !isTablet) ? 'small' : 'medium'}
                onClick={() => setCreatingNewTask(true)}
                disabled={!user_has_data_model_access(user, DataResources.DELIVERY_BATCH, ResourceActionType.CREATE)}
                variant="contained"
              >
                New Batch
              </Button>

              <Button
                size={(isMobile && !isTablet) ? 'small' : 'medium'}
                onClick={() => setReceivingFailedOrders(true)}
                variant="contained"
                color="error"
                disabled={!user_has_global_permission(user, GlobalPermissions.RECEIVE_FAILED_ORDERS)}
              >
                Receive Failed Orders
              </Button>
            </Grid>
            <Grid xs={12}>
              <Box>
                <Accordion elevation={0}>
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                  >
                    <Typography
                      variant="h5"
                      component="h5"
                      sx={{ textAlign: 'center', mt: 1, mb: 1 }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 5,
                        }}
                      >
                        <ManageSearch fontSize="large" color="primary" />
                        <span>
                          <b>Search and Filter Controls</b>
                        </span>
                      </div>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <DoubleDatePicker dateFilterAction={dateFilterAction} />
                    <br />
                    <FilterBar
                      filterQueryAction={filterQueryAction}
                      currentlySelectedFilters={filters}
                      filterKeys={DeliveryBatchFilterKeys}
                    />
                  </AccordionDetails>
                </Accordion>
                {isLoading ? (
                  <LinearProgress />
                ) : (
                  <>
                    {message != '' && (
                      <>
                        <Alert
                          severity={
                            response &&
                            response.status >= 200 &&
                            response.status < 300
                              ? 'success'
                              : 'error'
                          }
                        >
                          <>
                            {message}

                            {response && response.status >= 300 && (
                              <ErrorsFlattenner rawErrors={response.data} />
                            )}
                          </>
                        </Alert>
                        <br />{' '}
                      </>
                    )}

                    <div style={{ width: '100%', textAlign: 'center' }}>
                      <Chip
                        color="primary"
                        label={`${tasksCount} Tasks`}
                        variant={'outlined'}
                        sx={{ padding: 1 }}
                      />
                    </div>

                    <ResponsiveDeliveryBatchesListing
                      tasksList={tasks}
                      isLoading={isLoading}
                      setEditingTask={setEditingTask}
                      setDownloadBatchQrcode={setDownloadBatchQrcode}
                      setDownloadWaybillForTask={setDownloadWaybillForTask}
                      setCancellingTask={setCancellingTask}
                      setCompletingTask={setCompletingTask}
                      setDownloadReturnStatementsForTask={
                        setDownloadReturnStatementsForTask
                      }
                    />

                    <TablePagination
                      component="div"
                      onPageChange={handlePageChange}
                      page={controller.page}
                      count={tasksCount}
                      rowsPerPage={controller.rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[
                        5, 10, 25, 50, 100, 200, 250, 500, 1000,
                      ]}
                      labelRowsPerPage=""
                      style={{ overflow: 'visible' }}
                    />

                    {downloadWaybillForTask && (
                      <TaskWaybillsDialog
                        task={downloadWaybillForTask}
                        onClose={() => setDownloadWaybillForTask(undefined)}
                        mode={'orders'}
                      />
                    )}
                    {downloadReturnStatementsForTask && (
                      <TaskWaybillsDialog
                        task={downloadReturnStatementsForTask}
                        onClose={() =>
                          setDownloadReturnStatementsForTask(undefined)
                        }
                        mode={'returns'}
                      />
                    )}
                    {downloadBatchQrcode && (
                      <TaskWaybillsDialog
                        task={downloadBatchQrcode}
                        onClose={() => setDownloadBatchQrcode(undefined)}
                        mode="batch"
                      />
                    )}
                    {editingTask && (
                      <DeliveryBatchDialog
                        taskID={editingTask.id}
                        onClose={() => setEditingTask(undefined)}
                      />
                    )}

                    <ConfirmAction
                      message={'Are you sure you want to cancel this task?'}
                      action={handleCancelTask}
                      isOpen={cancellingTask != undefined}
                      handleClose={() => setCancellingTask(undefined)}
                      isSevereAction={true}
                      confirmSevereActionTarget={'cancel task'}
                    >
                      <Alert severity="warning">
                        This action can not be undone.
                      </Alert>
                    </ConfirmAction>


                     <ConfirmAction
                        message={'Are you sure you want to mark this batch as collected?'}
                        action={handleCompleteTask}
                        isOpen={completingTask != undefined}
                        handleClose={() => setCompletingTask(undefined)}
                      >
                        <Alert severity="warning">This action can not be undone.</Alert>
                      </ConfirmAction>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        }
      </Box>

      <CustomDialog
        open={creatingNewTask}
        fullWidth
        maxWidth="xl"
        fullScreen={(isMobile && !isTablet)}
        onClose={() => {
          setCreatingNewTask(false);
        }}
      >
        <div
          style={{
            textAlign: 'right',
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <IconButton
            onClick={() => {
              setCreatingNewTask(false);
            }}
          >
            <ClearIcon color="primary" fontSize="large" />
          </IconButton>
        </div>

        <DialogContent
          style={(isDesktop || isTablet) ? { width: '100%', height: '100%' } : {}}
        >
          <DeliveryBatchForm
            onSuccess={(b) => {
              showMessage(`Batch #${b.id} created`);
              const newTasks = [b, ...tasks];
              if (newTasks.length > paginationConfig.rowsPerPage) {
                newTasks.pop();
              } else {
                setTasksCount(tasksCount + 1);
              }
              setTasks(newTasks);
              setCreatingNewTask(false);
            }}
          />
        </DialogContent>
      </CustomDialog>

      <CustomDialog
        open={receivingFailedOrders}
        maxWidth="xl"
        fullScreen={(isMobile && !isTablet)}
        onClose={() => {
          setReceivingFailedOrders(false);
        }}
      >
        <div
          style={{
            textAlign: 'right',
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <IconButton
            onClick={() => {
              setReceivingFailedOrders(false);
            }}
          >
            <ClearIcon color="primary" fontSize="large" />
          </IconButton>
        </div>

        <DialogContent
          style={(isDesktop || isTablet) ? { width: '100%', height: '100%' } : {}}
        >
          <FailedDeliveriesReceiver />
        </DialogContent>
      </CustomDialog>
    </div>
  );
}

export default DeliveryBatchesListing;
